import React, { Component, createRef } from 'react';
import PropTypes from 'prop-types';
import { Row, Col } from '../../util_grid/grid';
import { Button, Icon, TitleAndSubtitle, PromoNotification, ProductImage, Badge, Rating, Excerpt, Metabar } from 'audamatic-ui';
import Link from '../../base_link/component';
import WishlistToggle from '../../ui_wishlist_toggle/component';
import AddToCartButton from '../../ui_add-to-cart-button/component';
import PriceBlock from '../../base_price-block/component';
import DeliveryPromise from '../../sec_delivery-promise/component';
import LoyaltyPrice from '@components/ui_loyalty-price/component';
import StockCheck from '../../sec_stock-check/component';
import { stripHTML, numFormatToCurrency } from '@utils';
import Text from '@text';
import { t } from '@utils/i18n';
import StockCounter from '@components/stock_counter/component';
import TypeSet from '@components/base_typeset/component';
import { IS_B2B } from '@config';
import HistoryToggle from '@components/ui_history_toggle/component';

import './styles/styles.scss';


/**
 * The ankeiler List is a variant of ankeiler (component).
 * You can render a ankeiler with variant = list and it will render this component
 *
 * It should be rendered inside a grid.
 *
 * This variant shows a tile with some highlights of a product.
 * Like image, name, availability, price etc and it can have an order button from the medium view and bigger
 *
 */
class AnkeilerList extends Component {
  constructor(props) {
    super(props);
    this.state = {
      removed: false,
      availibilityText: !this.product.isOnline ? t('pdp.product.not-available') : (this.product.fullProductAvailability || ''),
    };
    this.ankeilerRef = createRef();
  }

  componentDidMount() {
    this.setState({ forceRerender: true }); // Set ref at first render
  }

  pushGoogleTags = () => {
    this.props.googleTagMethods()
      .pushProductGoogleTag(this.props);
  };

  get product() {
    return this.props.product;
  }

  get name() {
    return this.product.name || '';
  }

  get title() {
    return this.props.title;
  }

  get url() {
    return this.product.url || '';
  }

  get image() {
    return this.product.thumbnail || '';
  }

  get sku() {
    return this.props.SKU || '';
  }

  get offered() {
    return this.props.offered || 0;
  }

  get type() {
    return this.props.type || '';
  }

  get action() {
    return this.props.action || '';
  }

  get availability() {
    if (this.offered === 4) {
      return { key: 'offered.storeOnly.msg' };
    }
    return { text: this.product.fullProductAvailability || '' };
  }

  get store() {
    return this.props.store || {};
  }

  get wishlist() {
    return this.props.wishlist;
  }

  // this description is not the actual product description
  get description() {
    return [this.props.descparttitle, this.props.descsectiontitle]
      .filter(Boolean)
      .join(', ');
  }

  get badges() {
    return Array.isArray(this.props.badges) ? this.props.badges : this.product.badges;
  }

  get regularPrices() {
    return this.product.price.regular || {};
  }

  removeFromWishlist = (e) => {
    e.preventDefault();

    // Set height to crrent height, to allow for height transition
    if (this.ankeilerRef?.current?.clientHeight) {
      this.ankeilerRef.current.style = `height: ${this.ankeilerRef.current.clientHeight}px`;

      // Optimistic update
      this.setState({ removed: true }, () => {
        this.ankeilerRef.current.style = `height: 0`;
        this.props.removeFromWishlist(this.sku);
      });
    }
  };

  get strippedLongDescription() {
    if (this.product.longDescription) {
      return stripHTML(this.product.longDescription);
    }
  }


  get productDescription() {
    return this.product.annotation || this.product.shortDescription || this.strippedLongDescription || '';
  }

  get rightOfReturn() {
    const { rightOfReturn } = this.product;
    if (!IS_B2B) {
      return null;
    }
    return (
      <div className="right-of-return"><TypeSet scale="0" tag="div"><Text>product.metadata.rightofreturn</Text></TypeSet>:&nbsp;
        <TypeSet scale="0" bold color="cta">
          {!!rightOfReturn ? (
            <Text>product.metadata.rightofreturn.yes</Text>
          ) : (
            <Text>product.metadata.rightofreturn.no</Text>
          )}
        </TypeSet>
      </div>
    );
  }

  get metaData() {
    const { type, rating, stockLevel, language, releaseDate, ean, sku, isCBProduct, isCBBuitenlandsProduct } = this.product;
    const items = [
      IS_B2B && !!language.length && language.map((language, i) => {
        return <div className="language" key={`language-${i}`}>{language}</div>;
      }),
      IS_B2B && <div className="ean">{ean || sku}</div>,
      IS_B2B && !!releaseDate && <div className="release-date">{releaseDate}</div>,
      !!type && <div className="producttype">{type}</div>,
      !!rating && <Rating size="small" rating={rating}/>,
      // Also check for IS_B2B here to prevent empty component from rendering a seperator when nothing is visible
      IS_B2B && !!stockLevel && stockLevel > 0 && !isCBProduct && !isCBBuitenlandsProduct && <StockCounter stockCount={stockLevel}/>,
      this.rightOfReturn,
    ];

    return (
      <div className="meta-data">
        <Metabar>
          {items.filter(Boolean)}
        </Metabar>
      </div>
    );
  }

  setAvailibilityText = (isExpressorder) => {
    if (isExpressorder) {
      this.setState({ availibilityText: t('expressorder.deliveryPromise') });
    } else {
      this.setState({ availibilityText: this.availability.text });
    }
  };

  /**
   * Set giftcard specific subtitle, when the giftcard does not have a fixed price.
   */
  get giftcardSubtitle() {
    const { giftcardDetails = {} } = this.product;

    const { fixed, min, max } = giftcardDetails;
    if (!fixed) {
      return (
        <Text replacements={{
          from: numFormatToCurrency(min),
          to: numFormatToCurrency(max),
        }}>blackhawk.price.range</Text>
      );
    }
    return false;
  }

  render() {
    const { removed, availibilityText } = this.state;
    const { badgeValue, badges, productHistory } = this.props;
    const { isGiftcard, giftcardDetails } = this.product;
    const showHistoryToggle = productHistory?.openQuantity > 0 || productHistory?.deliveredProducts.length > 0;
    return (
      <div ref={this.ankeilerRef} className={`ankeiler ankeiler-list ${removed ? 'removed' : ''}`}>
        {!!badges?.length && (
          <span className="tag-wrapper">
            <Badge variant="tag" text={<Text>comp.linebadge.promotion</Text>} size="tiny"/>
          </span>
        )}
        {this.wishlist ? (
          <div className="remove-from-wishlist-btn">
            <Button
              variant="secondary"
              size="small"
              iconLeft={<Icon name="Trash" size="sm" />}
              onClick={this.removeFromWishlist}
              data-test-id="button-removeFromWishlist"
            />
          </div>
        ) : (
          <div className="add-to-booklist-container" data-test-id="icon-addToWishlist">
            <WishlistToggle product={this.product} store={this.store} />
            {!!showHistoryToggle && (
              <HistoryToggle
                product={this.product}
                productHistory={productHistory}
              />
            )}
          </div>
        )}

        <Row>
          <Col xs={4} sm={2} className="image-column">
            <Link to={this.url} data-type="view" onClick={this.pushGoogleTags} className="product-link">
              <div className="ankeiler">
                <div className="image wrapper">
                  <ProductImage
                    product={this.product}
                    badge={badgeValue && <Badge number={badgeValue} size="small"/>}
                    maxHeight={149}
                    data-test-id="productImage"
                  />
                </div>
              </div>
            </Link>
          </Col>

          <Col xs={8} sm={6} className="details-column">
            <div className="haslink">
              <TitleAndSubtitle
                title={this.title}
                subtitle={isGiftcard ? this.giftcardSubtitle : this.product.personsLinks}
                data-test-id="titleAndSubtitle"
              />
            </div>
            {isGiftcard && (
              <div>
                <Text>{giftcardDetails.fixed ? 'blackhawk.price.fixed' : 'blackhawk.price.variable'}</Text>
              </div>
            )}
            {this.metaData}
            {!!this.badges.length && (
              <PromoNotification
                size="small"
                title={badges[0].title}
                subtitle={badges[0].subtitle}
                link={badges[0].link}
              />
            )}
            <span className="hidden-xs">
              <Excerpt
                showFormatting={true}
                href={this.product.url}
                text={this.productDescription}
                maxLines={3}
                label1={<Text>common.link.readmore</Text>}
              />
            </span>
          </Col>

          <Col xs={8} sm={4} className="purchase-details pull-right">
            <div className="ankeiler-buffer" />
            {this.product.isDeLoyaltyProduct ? (
              <LoyaltyPrice size="medium" prices={this.regularPrices} />
            ) : (
              <PriceBlock product={this.product} store={this.store}/>
            )}
          </Col>

          <Col xs={12} sm={4} className="purchase-details">
            <div className="button-row">
              {this.product.isGiftcard ? (
                <div className="giftcard-button">
                  <Link className="no-underline" to={this.url} onClick={this.pushGoogleTags}>
                    <Button variant="primary" size="large">
                      <Text>blackhawk.ankeiler.button.text</Text>
                    </Button>
                  </Link>
                </div>
              ) : (
                <AddToCartButton
                  hideIcon
                  store={this.store}
                  product={this.product}
                  showQuantity={true}
                  showOrderlineReference={true}
                  showExpressOrder={true}
                  toggleExpressOrder={this.setAvailibilityText}
                />
              )}

            </div>
            <div>
              {(!!this.availability && !this.product.isGiftcard) && (
                <DeliveryPromise
                  offered={this.product.offered}
                  textKey={this.availability.key}
                  text={availibilityText}
                  showIcon={false}
                />
              )}
            </div>
            {!this.product.isGiftcard && (
              <div className="stock-check-wrapper">
                <StockCheck store={this.store} product={this.product} showIcon={false} />
              </div>
            )}
          </Col>
        </Row>
      </div>
    );
  }
}


AnkeilerList.propTypes = {
  /**
   * The name of the product
   */
  name: PropTypes.string,
  /**
   * The url where to link to
   */
  url: PropTypes.string,
  /**
   * The image object with Thumbnail and Image
   */
  images: PropTypes.shape({
    Thumbnail: PropTypes.string,
    Image: PropTypes.string,
  }),
  /**
   * Pricing object.
   */
  price: PropTypes.shape({
    regular: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.array]),
  }),
  /**
   * Short description of the product
   */
  shortDescription: PropTypes.string,
  /**
   * Action text for the product
   */
  action: PropTypes.string,
  /**
   * Indicates if this ankeiler is shown in the wishlist
   */
  wishlist: PropTypes.bool,
  /**
   * Function to remove item from wishlist (only apply when props.wishlist === true)
   */
  removeFromWishlist: PropTypes.func,
  'image-shadow': PropTypes.any,
  SKU: PropTypes.string,
  offered: PropTypes.any,
  type: PropTypes.any,
  store: PropTypes.object,
  descparttitle: PropTypes.string,
  descsectiontitle: PropTypes.string,
  showPremiumBlock: PropTypes.bool,
  modal: PropTypes.object,
  googleTagMethods: PropTypes.func,
  availability: PropTypes.string,
  badgeValue: PropTypes.any,
  product: PropTypes.object,
  badges: PropTypes.array,
  toggleExpressOrder: PropTypes.bool,
  title: PropTypes.object,
  productHistory: PropTypes.object,
};


export default AnkeilerList;
